import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import Modal from '@mui/material/Modal';
import DropDown from './components/DropDown';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TextareaAutosize } from 'mui-compat/TextareaAutosize';
// import TextareaAutosize from '@mui/base/TextareaAutosize';
import PreviewIcon from '@mui/icons-material/Preview';
import Checkbox from '@mui/material/Checkbox';
import swal from 'sweetalert';
import Config from './components/ClientConfig';
import Client from './components/Client';
import OptimizedConfigImage, {
  usePreloadedOptimizedConfigImageSrc,
} from './components/OptimizedConfigImage';

import noImage from './images/noImage.png';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 550,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

function normalizeSavedImage(val) {
  try {
    if (!val) return '';
    let s = typeof val === 'string' ? val : (val.data ?? '');
    s = String(s).trim();
    if (!s) return '';
    if (s.startsWith('http://') || s.startsWith('https://')) return s;    // absolute
    if (s.startsWith('data:')) return s;                                   // data URI
    if (/^[A-Za-z0-9+/]+={0,2}$/.test(s)) return s;                        // bare base64
    // relative path → ensure leading slash
    if (s[0] !== '/') s = '/' + s;
    return s;
  } catch { return ''; }
}

function toBareBase64(input) {
  let s = String(input ?? '').trim();
  if (!s) return '';
  const comma = s.indexOf(',');
  if (s.startsWith('data:') && comma !== -1) s = s.slice(comma + 1);
  s = s.replace(/^base64,?/i, '').replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(s)) return '';
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  return s;
}

const MemoKImage = React.memo(function MemoKImage({
  edit,
  src,
  image,        // path | data-URI | bare base64 | {data}
  alt,
  imageText,
  saveBack,
  width = 128,
  height = 128,
}) {
  const srcStr = src ?? image;
  const altStr = alt ?? imageText;

  const isUrlOrPath =
    typeof srcStr === 'string' &&
    (srcStr.startsWith('/') || srcStr.startsWith('./') ||
     srcStr.startsWith('http://') || srcStr.startsWith('https://'));

  // Preload only in VIEW mode; keep hook order constant by passing null during edit
  const {
    ready,
    resolvedSrc: okSrc,
  } = usePreloadedOptimizedConfigImageSrc(edit ? null : srcStr, 3);

  // While preloading (view mode only), show a lightweight placeholder
  if (!ready) {
    return (
      <div
        style={{ width, height, background: '#e5e7eb', borderRadius: 6 }}
        aria-label="image loading"
      />
    );
  }

  if (edit) {
    const raw = (typeof srcStr === 'string' ? srcStr : '') || '';
    const previewSrc = raw
      ? (raw.startsWith('data:') || raw.startsWith('/') || raw.startsWith('http')
        ? raw
        : 'data:image/png;base64,' + (toBareBase64(raw) || noImage))
      : noImage;

    const handleFileChange = (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string' && saveBack) {
          saveBack(reader.result);
        }
      };
      reader.onerror = () => {
        swal("Error", "Image could not be loaded.", "error");
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width,
            height,
            border: '1px solid #d7dde5',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#f8fafc',
          }}
        >
          <img
            src={previewSrc}
            alt={altStr || ''}
            width={width}
            height={height}
            style={{ objectFit: 'contain', display: 'block' }}
          />
        </Box>
        <IconButton color="primary" aria-label="upload image" component="label" title="Upload original image">
          <FaPlusCircle />
          <input hidden accept="image/*" type="file" onChange={handleFileChange} />
        </IconButton>
      </Box>
    );
  }

  // ===== VIEW MODE: keep the stable, preloaded <img> to avoid warnings/rerenders =====
  if (isUrlOrPath) {
    return (
      <img
        src={okSrc || ('data:image/png;base64,' + noImage)}
        alt={altStr || ''}
        width={width}
        height={height}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    );
  }

  // base64/data in view mode → render as full data URL
  const dataUrl = 'data:image/png;base64,' + (toBareBase64(srcStr) || noImage);
  return (
    <img
      src={dataUrl}
      alt={altStr || ''}
      width={width}
      height={height}
      style={{ objectFit: 'contain', display: 'block' }}
    />
  );
}, (prev, next) =>
  prev.edit === next.edit &&
  prev.src === next.src &&
  prev.image === next.image &&
  prev.alt === next.alt &&
  prev.imageText === next.imageText &&
  prev.width === next.width &&
  prev.height === next.height &&
  prev.saveBack === next.saveBack
);

// ---------- TEXT ROW ----------
class DataRowText extends React.Component {
  constructor(props) {
    super(props);
    this.state = {   
      client: new Client ( "none" ),   
      edit: false,
      row: {},
      keyValid: false,
      valueValid: false,
      somethingHasChanged: false,
      showPreview: false
    };
    this.handleEditClick = this.handleEditClick.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);
    this.changeOption = this.changeOption.bind(this);
    this.closePreview = this.closePreview.bind(this);
    this.showPreview = this.showPreview.bind(this);
  }

  componentDidMount() {
    let row = this.props.row ? { ...this.props.row } : {};
    this.setState({ row });
    if (row.key) this.setState({ keyValid: true });
    if (row.value) this.setState({ valueValid: true });
    if (this.props.edit) this.setState({ edit: true });
  }

  handleEditClick() { this.setState({ edit: !this.state.edit }); }

  changeValue(e) {
    const id = e?.target?.id ?? null;
    const value = e?.target?.value ?? null;
    if (!id) return;
    const row = { ...this.state.row };
    if (id === 'key') { row.key = value || ''; this.setState({ keyValid: !!(value) }); }
    else if (id === 'value') { row.value = value || ''; this.setState({ valueValid: !!(value) }); }
    else if (id === 'description') { row.description = value || ''; }
    else if (id === 'exposeToFront') { row.exposeToFront = !row.exposeToFront; }
    this.setState({ row, somethingHasChanged: true });
  }

  pushData ( e ) {
    if ( this.state.somethingHasChanged && this.state.keyValid && this.state.valueValid ) {
      const apiUrl = this.props.apiUrl || Config.API_URL_CONFIG_MOBILEAPP_TEXT_ADD;
      let rdata = this.state.client.pushData ( apiUrl, this.state.row );
      if ( rdata ) {
        if ( rdata.status === "OK" ) {
          let uRow = this.state.row;
          uRow.iref = rdata.data?.iref;
          this.setState ( { row: uRow } );
          this.handleEditClick ( );
          swal ( "Success", "Successfully added/modified configuration value ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          })
          .then((value) => {
            //this.props.personUpdated ( );
            this.setState ( {edit: false} );
            this.setState ( { somethingHasChanged: false } );
            this.props.rowAdded ();
          });        
        } else {
          if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
          swal ( "Error", "Add/Update configuration value failed !\n\n" + rdata.message, "error" );
        }
      }
    } else {
      swal ( "Error", "Add/Update configuration value failed ! Some fields are not validated !", "error" );
    }
  }

  changeOption ( e ) {    
    if ( this.state.row ) {
      let uRow = this.state.row;
      let newLanguage = {};
      if ( this.props.languages ) {
        let langs = this.props.languages;  
        Object.keys(langs).forEach(key => {
          if ( key === e ) newLanguage = langs[ key ];
        });
      }
      uRow.language = newLanguage;
      this.setState ( { row: uRow } );
      this.setState ( { somethingHasChanged: true } );
    }
  }

  showPreview() { this.setState({ showPreview: true }); }
  closePreview() { this.setState({ showPreview: false }); }

  render() {
    let row = {};
    let iref = "0";
    let key = "";
    let dvalue = "";
    let value_show = "";
    let description = "";
    let description_show = "";
    let language = "";
    let language_text = "";
    let exposeToFront = false;
    if ( this.state.row ) {
      row = this.state.row;
      if ( row.iref ) iref = row.iref;
      if ( row.key ) key = row.key;
      if ( row.value ) {
        dvalue = row.value;
        value_show = dvalue;
        if ( dvalue.length > 32 ) value_show = dvalue.slice ( 0, 32 ) + " ...";
      }
      if ( row.description ) {
        description = row.description;
        description_show = description;
        if ( description.length > 48 ) description_show = description.slice(0, 48) + " ...";
      }
      if ( row.language ) {
        language = row.language.iref;
        language_text = row.language.languageName;
      }
      if ( row.exposeToFront === 1 || row.exposeToFront === "True" || row.exposeToFront === true ) exposeToFront = row.exposeToFront;
    }
    if ( this.props.dataRow !== null && this.props.dataRow !== undefined ) {
      row = this.props.dataRow;
    }

    let showSave = false;
    if ( this.state.edit === true && this.state.somethingHasChanged === true && this.state.keyValid === true && this.state.valueValid === true )
      showSave = true;

    let languages = [];
    if ( this.props.languages ) languages = this.props.languages;

    let showPreview = false;
    if ( this.state.showPreview ) showPreview = this.state.showPreview;

    return (
      <StyledTableRow key={row.iref}>
        <StyledTableCell align="left">{iref}</StyledTableCell>
        <StyledTableCell scope="row">
          {this.state.edit ? 
            <TextField id="key" label="Key" variant="outlined" value={key}
              onChange={this.changeValue} onFocus={e => e.target.select()}
              inputProps={{maxLength:"64"}} error={!this.state.keyValid} required sx={{minWidth: "360px"}} /> : key }
        </StyledTableCell>
        
        <StyledTableCell align="left">
          {this.state.edit ? 
            <TextareaAutosize id="value" label="Value" variant="outlined" value={dvalue}
              onChange={this.changeValue} onFocus={e => e.target.select()}
              minRows={5} maxRows={10} required /> : value_show}
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              value={description || ""}
              onChange={this.changeValue}
              multiline
              minRows={2}
              maxRows={4}
              sx={{ minWidth: "280px" }}
            />
            : description_show
          }
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?           
            <DropDown
              name="Language"
              value={language}              // iref string
              doptions={languages}          // object keyed by iref
              hasIcons={true}
              minWidth={100}
              onChangeFunction={this.changeOption}
            /> : language_text}
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?
            <Checkbox id="exposeToFront" color="success" checked={!!exposeToFront} onChange={this.changeValue}/>
            : <Checkbox color="success" checked={!!exposeToFront} disabled/>}
        </StyledTableCell>            
      
        <StyledTableCell align="right">
          <IconButton aria-label="settings" onClick={() => this.setState({showPreview:true})} title="Preview">
            <PreviewIcon />
          </IconButton>
          <IconButton aria-label="settings" onClick={this.handleEditClick} title="Edit">
            <EditIcon sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }} />
          </IconButton>
          {showSave && (
            <IconButton aria-label="settings" onClick={this.pushData} title="Save">
              <SaveIcon sx={{ color: "#4287f5" }} />
            </IconButton>
          )}
        </StyledTableCell>

        <StyledTableCell align="right" sx={{width: 0}}>
          <Modal open={showPreview} onClose={this.closePreview}>
            <Box sx={modalStyle}>
              <div dangerouslySetInnerHTML={{ __html: dvalue }} />
            </Box>
          </Modal>
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

// ---------- IMAGE ROW ----------
class DataRowImage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {   
      client: new Client ( "none" ),   
      edit: false,
      row: {},
      keyValid: false,
      somethingHasChanged: false,
      showPreview: false
    };
    this.handleEditClick = this.handleEditClick.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);
    this.changeOption = this.changeOption.bind(this);
    this.getImage = this.getImage.bind(this);
    this.closePreview = this.closePreview.bind(this);
    this.showPreview = this.showPreview.bind(this);
  }

  componentDidMount() {
    const row = this.props.row ? { ...this.props.row } : {};
    this.setState({ row, keyValid: !!(row.key) });
    if (this.props.edit) this.setState({ edit: true });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.row !== this.props.row && this.props.row) {
      const row = this.props.row;
      this.setState({
        row,
        keyValid: !!(row.key && row.key.length > 0),
        somethingHasChanged: false,
      });
    }
  }

  handleEditClick() { this.setState({ edit: !this.state.edit }); }

  getImage(newImage) {
    if (!newImage) return;
    const next = normalizeSavedImage(newImage);

    // If it’s a URL/path, test-load it first (race-safe after backend writes file)
    if (typeof next === 'string' &&
        (next.startsWith('/') || next.startsWith('./') || next.startsWith('http'))) {
      const probe = new Image();
      const done = (ok) => {
        const row = { ...(this.state.row || {}), image: ok ? next : (this.state.row?.image || next) };
        this.setState({ row, somethingHasChanged: true });
      };
      probe.onload = () => done(true);
      probe.onerror = () => {
        // keep showing previous (likely base64) preview; a later row reload will replace it
        done(false);
      };
      // small cache-bust to avoid stale CDN during the first seconds after save
      probe.src = next + (next.includes('?') ? '&' : '?') + 't=' + Date.now();
      return;
    }

    // data/base64 or empty → commit directly
    const row = { ...(this.state.row || {}), image: next || ('data:image/png;base64,' + noImage) };
    this.setState({ row, somethingHasChanged: true });
  }

  changeValue(e) {
    const id = e?.target?.id ?? null;
    const value = e?.target?.value ?? null;
    if (!id) return;
    const row = { ...this.state.row };
    if (id === 'key') { row.key = value || ''; this.setState({ keyValid: !!(value) }); }
    else if (id === 'description') { row.description = value || ''; }
    else if (id === 'exposeToFront') { row.exposeToFront = !row.exposeToFront; }
    this.setState({ row, somethingHasChanged: true });
  }

  pushData ( e ) {
    if ( this.state.somethingHasChanged && this.state.keyValid ) {
      const apiUrl = this.props.apiUrl || Config.API_URL_CONFIG_MOBILEAPP_IMAGE_ADD;
      let rdata = this.state.client.pushData ( apiUrl, this.state.row );
      if ( rdata ) {
        if ( rdata.status === "OK" ) {
          let uRow = this.state.row;
          uRow.iref = rdata.data?.iref;
          this.setState ( { row: uRow } );
          this.handleEditClick ( );
          swal ( "Success", "Successfully added/modified configuration value ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          })
          .then((value) => {
            //this.props.personUpdated ( );
            this.setState ( {edit: false} );
            this.setState ( { somethingHasChanged: false } );
            this.props.rowAdded ();
          });        
        } else {
          if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
          swal ( "Error", "Add/Update configuration value failed !\n\n" + rdata.message, "error" );
        }
      }
    } else {
      swal ( "Error", "Add/Update configuration value failed ! Some fields are not validated !", "error" );
    }
  }

  changeOption ( e ) {    
    if ( this.state.row ) {
      let uRow = this.state.row;
      let newLanguage = {};
      if ( this.props.languages ) {
        let langs = this.props.languages;  
        Object.keys(langs).forEach(key => {
          if ( key === e ) newLanguage = langs[ key ];
        });
      }
      uRow.language = newLanguage;
      this.setState ( { row: uRow } );
      this.setState ( { somethingHasChanged: true } );
    }
  }

  showPreview() { this.setState({ showPreview: true }); }
  closePreview() { this.setState({ showPreview: false }); }

  render() {
    let row = {};
    let iref = "0";
    let key = "";
    let value = "";
    let description = "";
    let description_show = "";
    let image = noImage;
    let imageFullURL = "";
    let language = "";
    let language_text = "";
    let exposeToFront = false;
    if ( this.state.row ) {
      row = this.state.row;
      if ( row.iref ) iref = row.iref;
      if ( row.key ) key = row.key;
      if ( row.value ) value = row.value;
      if ( row.description ) {
        description = row.description;
        description_show = description;
        if ( description.length > 48 ) description_show = description.slice(0, 48) + " ...";
      }
      if ( row.image ) {
        image = row.image; 
        // imageFullURL = window.location.protocol + "//" + window.location.host + image;
        imageFullURL = row.image
          ? (String(row.image).startsWith('http') ? String(row.image)
            : window.location.origin + (String(row.image).startsWith('/') ? String(row.image) : '/' + String(row.image)))
          : '';
      }
      if ( row.language ) {
        language = row.language.iref;
        language_text = row.language.languageName;
      }    
      if ( row.exposeToFront === 1 || row.exposeToFront === "True" || row.exposeToFront === true ) exposeToFront = row.exposeToFront;
    }
    if ( this.props.dataRow !== null && this.props.dataRow !== undefined ) {
      row = this.props.dataRow;
    }

    let imageName = key;

    let showSave = false;
    if ( this.state.edit === true && this.state.somethingHasChanged === true && this.state.keyValid === true )
      showSave = true;

    let languages = [];
    if ( this.props.languages ) languages = this.props.languages;

    let showPreview = false;
    if ( this.state.showPreview ) showPreview = this.state.showPreview;

    return (
      <StyledTableRow key={row.iref}>
        <StyledTableCell align="left">{iref}</StyledTableCell>
        <StyledTableCell scope="row">
          {this.state.edit ? 
            <TextField id="key" label="Key" variant="outlined" value={key}
              onChange={this.changeValue} onFocus={e => e.target.select()}
              inputProps={{maxLength:"64"}} error={!this.state.keyValid} required sx={{minWidth: "360px"}} /> : key }
        </StyledTableCell>
        
        <StyledTableCell align="left">
          {/* Defer load + prevent invalid src on mount */}
          <MemoKImage
            edit={this.state.edit}
            src={
              !this.state.edit && typeof image === 'string' && (image.startsWith('/') || image.startsWith('http'))
                ? `${image}?v=${row.iref}-${row.modifiedOn || Date.now()}`
                : image
            }
            imageText={imageName}
            saveBack={this.getImage}
            width={128}
            height={128}
          />
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              value={description || ""}
              onChange={this.changeValue}
              multiline
              minRows={2}
              maxRows={4}
              sx={{ minWidth: "280px" }}
            />
            : description_show
          }
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?           
            <DropDown
              name="Language"
              value={language}
              doptions={languages}
              hasIcons={true}
              minWidth={100}
              onChangeFunction={this.changeOption}
            /> : language_text}
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ?
            <Checkbox id="exposeToFront" color="success" checked={!!exposeToFront} onChange={this.changeValue}/>
            : <Checkbox color="success" checked={!!exposeToFront} disabled/>}
        </StyledTableCell>

        <StyledTableCell align="right">
          <IconButton aria-label="settings" onClick={() => this.setState({showPreview:true})} title="Preview">
            <PreviewIcon />
          </IconButton>
          <IconButton aria-label="settings" onClick={this.handleEditClick} title="Edit">
            <EditIcon sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }} />
          </IconButton>
          {showSave && (
            <IconButton aria-label="settings" onClick={this.pushData} title="Save">
              <SaveIcon sx={{ color: "#4287f5" }} />
            </IconButton>
          )}
        </StyledTableCell>

        <StyledTableCell align="right" sx={{width: 0}}>
          <Modal open={showPreview} onClose={this.closePreview}>
            <Box sx={modalStyle}>
              <Box
                component={OptimizedConfigImage}
                height={512}
                sx={{ width: 512, marginLeft: 1 }}
                src={
                  (typeof image === 'string' && (image.startsWith('/') || image.startsWith('http')))
                    ? image
                    : ('data:image/png;base64,' + (toBareBase64(image) || noImage))
                }
                alt="preview"
              />
              <Typography variant="subtitle1" sx={{fontSize: "0.7em"}}>{imageFullURL}</Typography>
            </Box>
          </Modal>
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

// ---------- PAGE ----------
class ConfigMobileIdentity extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client ( "none" ),
      rowsText: null,
      rowsImage: null,
      editText: false,
      editImage: false,
      addRowText: false,
      queryDataText: {},
      queryDataImage: {},
      expandedText: false,
      expandedImage: false
    };
    this.rowTextAdded = this.rowTextAdded.bind(this);
    this.rowImageAdded = this.rowImageAdded.bind(this);
    this.toggleAddText = this.toggleAddText.bind(this);
    this.toggleAddImage = this.toggleAddImage.bind(this);
    this.getDataText = this.getDataText.bind(this);
    this.getDataImage = this.getDataImage.bind(this);
    this.expandTextPanel = this.expandTextPanel.bind(this);
    this.expandImagePanel = this.expandImagePanel.bind(this);
    this.textTableRef = React.createRef();
    this.imageTableRef = React.createRef();
  }

  getDataText() {
    const queryData = { orderBy: "key" };
    this.setState({ queryData });
    const apiUrl = this.props.textApiUrl || Config.API_URL_CONFIG_MOBILEAPP_TEXT;
    const responseKey = this.props.textResponseKey || "MobileAppTextConfigs";
    const data = this.state.client.getData(apiUrl, queryData);
    this.setState({ rowsText: data?.[responseKey] || [] });
  }
  getDataImage() {
    const queryData = { orderBy: "key" };
    this.setState({ queryData });
    const apiUrl = this.props.imageApiUrl || Config.API_URL_CONFIG_MOBILEAPP_IMAGE;
    const responseKey = this.props.imageResponseKey || "MobileAppImageConfigs";
    const data = this.state.client.getData(apiUrl, queryData);
    this.setState({ rowsImage: data?.[responseKey] || [] });
  }

  componentDidMount() {
    this.getDataText();
    this.getDataImage();
  }

  rowTextAdded() {
    this.getDataText();
    this.setState({ addRowText: false });
    if (this.props.refreshLocalConfig !== false) {
      this.state.client.setConfigLocalStorage();
      window.dispatchEvent(new Event("storageUpdate"));
    }
  }
  rowImageAdded() {
    this.getDataImage();
    this.setState({ addRowImage: false });
    if (this.props.refreshLocalConfig !== false) {
      this.state.client.setConfigLocalStorage();
      window.dispatchEvent(new Event("storageUpdate"));
    }
  }

  toggleAddText() {
    const was = this.state.addRowText;

    this.setState({ addRowText: !was }, () => {
      // scroll the table container (if it is the scroll parent)
      const el = this.textTableRef?.current;
      if (!was && el) {
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }

      // fallback: if the PAGE is actually scrolling (common with Accordions/layouts)
      if (!was && (!el || el.scrollTop === 0)) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  toggleAddImage() {
    const was = this.state.addRowImage;

    this.setState({ addRowImage: !was }, () => {
      const el = this.imageTableRef?.current;
      if (!was && el) {
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }

      if (!was && (!el || el.scrollTop === 0)) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  expandTextPanel() { this.setState({ expandedText: !this.state.expandedText }); }
  expandImagePanel() { this.setState({ expandedImage: !this.state.expandedImage }); }

  render() {

    let languages = {};
    if ( this.props.languages ) {
      let langs = this.props.languages;
      Object.keys(langs).forEach(key => {
        let fData = langs[ key ];
        let newObj = { iref: fData.iref, name: fData.languageName, image: fData.image, languageName: fData.languageName, };
        languages[ fData.iref ] = newObj;
      });
    }

    let allDataText ="";
    if ( this.state.rowsText ) {
      //console.log ( this.state.rows );
      if ( this.state.rowsText.length > 0 ) {
        let rowsText = this.state.rowsText;
        allDataText = rowsText.map((rowText) => {
          return <DataRowText
            key={`${rowText.iref}-${rowText.key}-${rowText.value}-${rowText.description || ""}`}
            row = {rowText}
            rowAdded={this.rowTextAdded}
            languages = {languages}
            apiUrl={this.props.textAddApiUrl}
          />
        });
      }      
    }

    let allDataImage ="";
    if ( this.state.rowsImage ) {
      //console.log ( this.state.rows );
      if ( this.state.rowsImage.length > 0 ) {
        let rowsImage = this.state.rowsImage;
        allDataImage = rowsImage.map((rowImage) => {
          return <DataRowImage
            key={`${rowImage.iref}-${rowImage.key}-${(rowImage.image || '').slice(-16)}-${rowImage.description || ""}`}
            row = {rowImage}
            rowAdded={this.rowImageAdded}
            languages = {languages}
            apiUrl={this.props.imageAddApiUrl}
          />
        });
      }      
    }

    let expandedText = false;
    if ( this.state.expandedText ) expandedText = this.state.expandedText;
    let expandedImage = false;
    if ( this.state.expandedImage ) expandedImage = this.state.expandedImage;

    return (
      <div>
        <Accordion id="textAccordion" expanded={expandedText} onChange={this.expandTextPanel} sx={{backgroundColor: "#2e3b49", marginTop: 2}}>
          <AccordionSummary sx={{color: "#adadad"}}
            expandIcon={<ExpandMoreIcon id="textAccordion" sx={{color: "#adadad"}}/>}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0, color: "#adadad" }}>
              {this.props.identityLabel || "Mobile App Configuration"}
            </Typography>
            <Typography sx={{ color: '#adadad' }}>Texts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ maxHeight: '75vh', overflow: 'auto' }} ref={this.textTableRef}>
              <Table stickyHeader sx={{ minWidth: 980 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <IconButton aria-label="Add" title="Add" onClick={() => this.toggleAddText()}>       
                        <Avatar alt="Add" sx={{ width: 24, height: 24, color: "#273442" }}>
                          {this.state.addRowText ? <FaMinusCircle/> : <FaPlusCircle/>}
                        </Avatar>
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell>Key</StyledTableCell>
                    <StyledTableCell align="left">Value</StyledTableCell>
                    <StyledTableCell align="left">Description</StyledTableCell>
                    <StyledTableCell align="left">Language</StyledTableCell>
                    <StyledTableCell align="left">Expose To Front</StyledTableCell>
                    <StyledTableCell align="right">Action</StyledTableCell>
                    <StyledTableCell align="right" sx={{width: 0}}></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.state.addRowText ?
                    <DataRowText key={0} edit={true} rowAdded={this.rowTextAdded} languages={languages} apiUrl={this.props.textAddApiUrl}/>
                  : null}
                  {allDataText}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion id="imageAccordion" expanded={expandedImage} onChange={this.expandImagePanel} sx={{backgroundColor: "#2e3b49", marginTop: 1}}>
          <AccordionSummary sx={{color: "#adadad"}}
            expandIcon={<ExpandMoreIcon id="imageAccordion" sx={{color: "#adadad"}}/>}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0, color: "#adadad" }}>
              {this.props.identityLabel || "Mobile App Configuration"}
            </Typography>
            <Typography sx={{ color: "#adadad" }}>Images</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ maxHeight: '75vh', overflow: 'auto' }} ref={this.imageTableRef}>
              <Table stickyHeader sx={{ minWidth: 980 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <IconButton aria-label="Add" title="Add" onClick={() => this.toggleAddImage()}>       
                        <Avatar alt="Add" sx={{ width: 24, height: 24, color: "#273442" }}>
                          {this.state.addRowImage ? <FaMinusCircle/> : <FaPlusCircle/>}
                        </Avatar>
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell>Key</StyledTableCell>
                    <StyledTableCell align="left">Image</StyledTableCell>
                    <StyledTableCell align="left">Description</StyledTableCell>
                    <StyledTableCell align="left">Language</StyledTableCell>
                    <StyledTableCell align="left">Expose To Front</StyledTableCell>
                    <StyledTableCell align="right">Action</StyledTableCell>
                    <StyledTableCell align="right" sx={{width: 0}}></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.state.addRowImage ?
                    <DataRowImage key={0} edit={true} rowAdded={this.rowImageAdded} languages={languages} apiUrl={this.props.imageAddApiUrl}/>
                  : null}
                  {allDataImage}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </div>      
    );
  }
}

export default ConfigMobileIdentity;
